import { useContext, useEffect, useState } from 'react';
import { Target, CheckCircle, AlertTriangle, TrendingUp, FileText, Users, Calendar, Clock } from 'lucide-react';
import AuthContext from '../../../../context/AuthContext';
import auditManagerDashboardService from '../../../../services/audit_services/auditManagerDashboardService';
import auditHistoryService from '../../../../services/audit_services/auditHistoryService';

export default function AuditManagerDashboardHome() {
  const { authTokens } = useContext(AuthContext);
  const [dashboardData, setDashboardData] = useState(null);
  const [recentAudits, setRecentAudits] = useState([]);
  const [recentRecommendations, setRecentRecommendations] = useState([]);
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch dashboard statistics
        const statsResponse = await auditManagerDashboardService.getDashboardStats(authTokens.access);
        if (!statsResponse.success) {
          throw new Error(statsResponse.error);
        }

        // Fetch recent audits
        const auditsResponse = await auditManagerDashboardService.getRecentAudits(authTokens.access);
        if (!auditsResponse.success) {
          throw new Error(auditsResponse.error);
        }

        // Fetch recent recommendations
        const recommendationsResponse = await auditManagerDashboardService.getRecentRecommendations(authTokens.access);
        if (!recommendationsResponse.success) {
          throw new Error(recommendationsResponse.error);
        }

        // Fetch recent history
        const historyResponse = await auditHistoryService.getRecentAuditHistory(authTokens.access);
        if (historyResponse.success) {
          setHistoryData(historyResponse.data);
        }
        
        setDashboardData(statsResponse.data);
        setRecentAudits(auditsResponse.data.recent_audits || []);
        setRecentRecommendations(recommendationsResponse.data.recent_recommendations || []);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (authTokens?.access) {
      fetchDashboardData();
    }
  }, [authTokens?.access]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <AlertTriangle className="h-12 w-12 mx-auto" />
          </div>
          <p className="text-red-600 font-medium">Erreur de chargement du tableau de bord</p>
          <p className="text-gray-600 text-sm mt-2">{error}</p>
        </div>
      </div>
    );
  }

  const stats = dashboardData || {};
  const auditPrograms = stats.audit_programs || {};
  const auditPlans = stats.audit_plans || {};
  const recommendationActions = stats.recommendation_actions || {};

  return (
    <div className="space-y-6">
      {/* Welcome Message */}
      <div className="md:flex justify-between items-center bg-teal-50 border border-teal-200 rounded-md p-2">
        <h2 className="text-base font-semibold text-gray-800">
          Tableau de bord Gestionnaire d'Audit 🎯
        </h2>
        <p className="text-gray-600 text-sm">
          Gérer et superviser tous les audits ISO 19011
        </p>
      </div>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Audit Programs */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Programmes d'audit</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{auditPrograms.total || 0}</p>
              <div className="mt-2 flex items-center space-x-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                  Total programmes
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <FileText className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Total Audit Plans */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Plans d'audit totaux</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{auditPlans.total || 0}</p>
              <div className="mt-2 flex items-center space-x-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-800">
                  {auditPlans.in_progress || 0} en cours
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
              <Target className="h-6 w-6 text-teal-600" />
            </div>
          </div>
        </div>

        {/* Total Recommendation Actions */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Actions de recommandation</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{recommendationActions.total || 0}</p>
              <div className="mt-2 flex items-center space-x-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  {recommendationActions.completed || 0} complétées
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Overdue Actions */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Actions en retard</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{recommendationActions.overdue || 0}</p>
              <div className="mt-2 flex items-center space-x-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                  Nécessite attention
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Audit Plans Status */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-700">Statut des plans d'audit</h4>
            <Target className="h-4 w-4 text-teal-500" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">En attente</span>
              <span className="text-xs font-semibold text-orange-600">{auditPlans.pending || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">En cours</span>
              <span className="text-xs font-semibold text-teal-600">{auditPlans.in_progress || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Complétés</span>
              <span className="text-xs font-semibold text-green-600">{auditPlans.completed || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Fermés</span>
              <span className="text-xs font-semibold text-gray-600">{auditPlans.closed || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Atteints</span>
              <span className="text-xs font-semibold text-purple-600">{auditPlans.achieved || 0}</span>
            </div>
          </div>
        </div>

        {/* Recommendation Actions Status */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-700">Actions de recommandation</h4>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">En attente</span>
              <span className="text-xs font-semibold text-orange-600">{recommendationActions.pending || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">En cours</span>
              <span className="text-xs font-semibold text-teal-600">{recommendationActions.in_progress || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Complétées</span>
              <span className="text-xs font-semibold text-green-600">{recommendationActions.completed || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Atteintes</span>
              <span className="text-xs font-semibold text-purple-600">{recommendationActions.achieved || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">En retard</span>
              <span className="text-xs font-semibold text-red-600">{recommendationActions.overdue || 0}</span>
            </div>
          </div>
        </div>
      </div>


      {/* Recent Audits Table */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <h4 className="text-sm font-semibold text-gray-700 mb-4">Audits récents</h4>
        <div className="overflow-x-auto">
          {recentAudits.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Plan d'audit</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Programme</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Créé par</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mis à jour</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {recentAudits.map((audit) => (
                  <tr key={audit.id}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="font-medium">{audit.title}</div>
                      {audit.objectives && (
                        <div className="text-xs text-gray-500 truncate max-w-xs">
                          {audit.objectives}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {audit.audit_program?.name || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {audit.created_by?.name || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {audit.updated_at ? new Date(audit.updated_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        audit.status === 'completed' ? 'bg-green-100 text-green-800' :
                        audit.status === 'in_progress' ? 'bg-teal-100 text-teal-800' :
                        audit.status === 'pending' ? 'bg-orange-100 text-orange-800' :
                        audit.status === 'closed' ? 'bg-gray-100 text-gray-800' :
                        audit.status === 'achieved' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {audit.status_display || audit.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Aucun audit récent trouvé</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Recommendations Assigned to Me */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <h4 className="text-sm font-semibold text-gray-700 mb-4">Mes recommandations récentes</h4>
        <div className="overflow-x-auto">
          {recentRecommendations.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Recommandation</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date cible</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mis à jour</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {recentRecommendations.map((rec) => (
                  <tr key={rec.id}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="font-medium truncate max-w-md">{rec.recommendation_text}</div>
                      {rec.elements_of_recommendation && (
                        <div className="text-xs text-gray-500 truncate max-w-md">
                          {rec.elements_of_recommendation}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {rec.delay_of_recommendation ? new Date(rec.delay_of_recommendation).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {rec.updated_at ? new Date(rec.updated_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        rec.status === 'completed' ? 'bg-green-100 text-green-800' :
                        rec.status === 'in_progress' ? 'bg-teal-100 text-teal-800' :
                        rec.status === 'pending' ? 'bg-orange-100 text-orange-800' :
                        rec.status === 'achieved' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {rec.status_display || rec.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Aucune recommandation récente trouvée</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity History */}
      {historyData && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-gray-700">Historique d'activité récente</h4>
            <Clock className="h-4 w-4 text-gray-500" />
          </div>
          
          <div className="space-y-4">
            {/* User Activity */}
            {historyData.user_activity_history && historyData.user_activity_history.length > 0 && (
              <div>
                <h5 className="text-xs font-semibold text-gray-600 mb-2">Mes actions récentes</h5>
                <div className="space-y-2">
                  {historyData.user_activity_history.map((activity) => (
                    <div key={activity.id} className="flex items-start space-x-3 p-2 bg-gray-50 rounded">
                      <div className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-teal-500"></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-900">{activity.description}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {new Date(activity.action_date).toLocaleString()}
                        </p>
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-800">
                        {activity.action_type}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Combined Audit History */}
            {(() => {
              const allHistory = [
                ...(historyData.audit_program_history || []).map(h => ({ ...h, type: 'Programme' })),
                ...(historyData.audit_plan_history || []).map(h => ({ ...h, type: 'Plan' })),
                ...(historyData.audit_section_history || []).map(h => ({ ...h, type: 'Section' })),
                ...(historyData.audit_question_history || []).map(h => ({ ...h, type: 'Question' })),
                ...(historyData.recommendation_action_history || []).map(h => ({ ...h, type: 'Recommandation' })),
                ...(historyData.recommendation_followup_history || []).map(h => ({ ...h, type: 'Suivi' }))
              ].sort((a, b) => new Date(b.action_date) - new Date(a.action_date)).slice(0, 10);

              return allHistory.length > 0 ? (
                <div>
                  <h5 className="text-xs font-semibold text-gray-600 mb-2">Activité d'audit de l'organisation</h5>
                  <div className="space-y-2">
                    {allHistory.map((activity, index) => (
                      <div key={`${activity.type}-${activity.id}-${index}`} className="flex items-start space-x-3 p-2 bg-gray-50 rounded">
                        <div className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-teal-500"></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-900">{activity.description}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {activity.done_by_name || activity.done_by_email || 'Système'} • {new Date(activity.action_date).toLocaleString()}
                          </p>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-800">
                          {activity.type}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
